export type UsageDurationRow = {
  startTime: string;
  endTime?: string | null;
  active: boolean;
  groupId?: string | null;
  groupName?: string | null;
};

export function durationSeconds(
  row: Pick<UsageDurationRow, "startTime" | "endTime" | "active">,
  now: Date = new Date()
): number | null {
  const start = new Date(row.startTime);
  const end = row.endTime ? new Date(row.endTime) : row.active ? now : null;
  if (!end) return null;
  const seconds = Math.floor((end.getTime() - start.getTime()) / 1000);
  return Number.isFinite(seconds) ? Math.max(0, seconds) : null;
}

export function formatDurationShort(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return "0s";

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);

  return parts.join(" ") || "0s";
}

export function formatPercentOfTotal(
  seconds: number | null,
  totalSeconds: number
): string {
  if (seconds == null) return "—";
  if (!Number.isFinite(seconds) || seconds <= 0) return "—";
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return "0%";
  const percent = (seconds / totalSeconds) * 100;
  if (!Number.isFinite(percent)) return "0%";
  return `${percent.toFixed(percent >= 10 ? 0 : 1)}%`;
}

export type GroupAggregate = {
  groupId: string;
  groupName: string;
  seconds: number;
};

export function aggregateSecondsByGroup(
  rows: UsageDurationRow[],
  now: Date = new Date()
): GroupAggregate[] {
  const map = new Map<string, GroupAggregate>();
  for (const row of rows) {
    const seconds = durationSeconds(row, now);
    if (!seconds || seconds <= 0) continue;

    const groupId = row.groupId || "unknown";
    const groupName = row.groupName || "<Blank>";
    const existing = map.get(groupId);
    if (existing) {
      existing.seconds += seconds;
    } else {
      map.set(groupId, { groupId, groupName, seconds });
    }
  }

  return [...map.values()].sort((a, b) => b.seconds - a.seconds);
}

